import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { CONSTANTS } from '../../../../Core/Constant';
import { environment } from 'src/environments/environment';
import { TranslationService } from 'src/app/Service/translation.service';


@Component({
  selector: 'app-physical-ability',
  templateUrl: './physical-ability.component.html',
  styleUrls: ['./physical-ability.component.scss']
})
export class PhysicalAbilityComponent {
  @Input() applicantData: any; // Accept data from parent component
  @Input() isPreviewMode: boolean = false; // Flag to determine behavior
  @Output() formGroupChange = new EventEmitter<FormGroup>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  physicalAbilityForm!: FormGroup;
  submitted = false;
  SaveMode = CONSTANTS.SAVE_MODE;
  isJsonShow = environment.isJsonShow;  // Set isJsonShow from the environment
  isEditable: boolean = true; // Flag to track if the form is editable


  constructor(private toastr: ToastrService, private api: SevaProfileService, private fb: FormBuilder, public genericValidations: GenericValidationsService, private translation: TranslationService) { }

  ngOnInit(): void {

    if (!this.isPreviewMode) {
      // Execute only when not in preview mode
      this.initializeForm();
      this.getApplicantData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applicantData'] && this.applicantData) {

      this.initializeForm();
      this.physicalAbilityForm.patchValue(this.applicantData);
      this.physicalAbilityForm.disable();
    }
  }

  getApplicantData() {
    this.api.apiCall('PhysicalAbility/GetPhysicalAbility', 'GET').subscribe((response: any) => {
      if (response.Data && Object.keys(response.Data).length > 0) {
        this.genericValidations.setProgressPercentage(response.Data.CompletionPercentage); // Example: Set progress to 50%

        // Store IsEditable flag from response
        this.isEditable = response.Data.IsEditable !== false; // Default to true if not provided

        // Disable form if not editable
        if (!this.isEditable) {
          this.physicalAbilityForm.disable();
        } else {
          this.physicalAbilityForm.enable();
        }

        this.physicalAbilityForm.patchValue(response.Data);
      }
    })
  }

  initializeForm() {
    this.physicalAbilityForm = this.fb.group({
      HasPainNeckOrShoulders: [null, Validators.required],
      HasPainBack: [null, Validators.required],
      HasPainKnees: [null, Validators.required],
      HasPainHips: [null, Validators.required],
      HasPainFeet: [null, Validators.required],
      HasPainHandsWristsOrArms: [null, Validators.required],
      PhysicalLimitationsDetails: [''],
      HasRepetitiveStrainInjury: [null, Validators.required],
      RepetitiveStrainInjuryDetails: [''],
      AffectedAreas: [''],
      SaveMode: ['', [Validators.required,]],
    });

    this.abilityListner();

    const controlMappings = {
      'HasRepetitiveStrainInjury': ['RepetitiveStrainInjuryDetails', 'AffectedAreas'],
    }

    // Call the service with the isShowOn parameter set to true or false as needed
    this.genericValidations.setupFormListeners(this.physicalAbilityForm, controlMappings, true);
    // Emit the form group to parent component
    this.formGroupChange.emit(this.physicalAbilityForm);
  }




  abilityListner() {
    // Monitor the fields to dynamically toggle required validator
    this.physicalAbilityForm.get('HasPainNeckOrShoulders')?.valueChanges.subscribe(() => this.togglePhysicalLimitationsValidator());
    this.physicalAbilityForm.get('HasPainBack')?.valueChanges.subscribe(() => this.togglePhysicalLimitationsValidator());
    this.physicalAbilityForm.get('HasPainKnees')?.valueChanges.subscribe(() => this.togglePhysicalLimitationsValidator());
    this.physicalAbilityForm.get('HasPainHips')?.valueChanges.subscribe(() => this.togglePhysicalLimitationsValidator());
    this.physicalAbilityForm.get('HasPainFeet')?.valueChanges.subscribe(() => this.togglePhysicalLimitationsValidator());
    this.physicalAbilityForm.get('HasPainHandsWristsOrArms')?.valueChanges.subscribe(() => this.togglePhysicalLimitationsValidator());
  }


  onSubmit(Mode: number): void {

    // this.physicalAbilityForm.patchValue({
    //   SaveMode: Mode
    // })

    if (!this.physicalAbilityForm.valid && Mode == this.SaveMode.SAVE) {
      this.submitted = true;


      // Add 'submitted' class manually to all invalid controls
      Object.keys(this.physicalAbilityForm.controls).forEach((field) => {
        const control = this.physicalAbilityForm.get(field);
        if (control && control.invalid) {
          control.markAsTouched(); // Ensure ng-touched is triggered
          const element = document.getElementById(field);
          if (element) {
            element.classList.add('submitted'); // Add 'submitted' class to invalid elements
          }
        }
      });

      this.toastr.warning(this.getTranslation('validation.completeAllFields'));

      return
    }


    const cleanedValues = this.genericValidations.removeWhitespaceFromValues(this.physicalAbilityForm.value);
    this.addUpdate(cleanedValues, Mode);


  }


  addUpdate(reqBoby: any, Mode: number) {
    reqBoby.SaveMode = Mode;
    this.api.apiCall('PhysicalAbility/SavePhysicalAbility', 'POST', reqBoby).subscribe((response: any) => {
      console.log('POST response:', response);
      this.toastr.success(response?.Message);
      if (Mode == this.SaveMode.SAVE) {
        this.nextStep.emit();
      } else if (Mode == this.SaveMode.DRAFT) {
        this.getApplicantData();
      }
    });
  }

  goToBack(): void {
    this.previous.emit();
  }

  // Check if any 'Yes' is selected for physical limitations
  hasPhysicalLimitations(): boolean {
    return this.physicalAbilityForm.get('HasPainNeckOrShoulders')?.value === true ||
      this.physicalAbilityForm.get('HasPainBack')?.value === true ||
      this.physicalAbilityForm.get('HasPainKnees')?.value === true ||
      this.physicalAbilityForm.get('HasPainHips')?.value === true ||
      this.physicalAbilityForm.get('HasPainFeet')?.value === true ||
      this.physicalAbilityForm.get('HasPainHandsWristsOrArms')?.value === true;
  }

  togglePhysicalLimitationsValidator(): void {
    const physicalLimitationsControl = this.physicalAbilityForm.get('PhysicalLimitationsDetails');

    if (this.hasPhysicalLimitations()) {
      physicalLimitationsControl?.setValidators([Validators.required]);
    } else {
      physicalLimitationsControl?.clearValidators();
    }

    physicalLimitationsControl?.updateValueAndValidity();
  }

  // Translation helper to mirror usage pattern in other components
  getTranslation(key: string): string {
    return this.translation.getTranslation(key);
  }
}